/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useApolloClient } from '@apollo/client';
import { usePrevious } from 'rooks';

// app modules: actions
import { setRecipe } from 'actions/homeconnect';
import { setAdvertVisibility } from 'actions/adverts';
import { showMeteredAccessBanner, hideMeteredAccessBanner } from 'actions/meteredAccess';
import { setSelectedTableOfContents } from 'actions/tableOfContents';

// app: components
import ErrorRouter from 'components/ErrorRouter';
import HeadContent from 'components/HeadContent';
import Recipe from 'components/Recipe';
import Reference from 'components/Reference';
import Section from 'components/Section';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

import RecipeQuery from './RecipeQuery.gql';
import ContentItemQuery from './ContentItemQuery.gql';
import MeteredAccessQuery from './MeteredAccessQuery.gql';
import RecipePreviewFragment from './RecipePreview.gql';
import ContentPreviewFragment from './ContentPreview.gql';
import BookPreviewFragment from './BookPreview.gql';

const ContentView = props => {
  const { id, type, token, isInitialPage, previewOnly, enableSidebarAd, forceSsr } = props;

  // Keep track of whether we are mounted. This allows us to optimise SSR and initial render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const dispatch = useDispatch();
  const displayType = useSelector(state => state.dimensions.displayType);
  const clientId = useSelector(state => state.device.clientId);
  const isLoggedIn = useSelector(state => state.user.logged_in);
  const clockOffset = useSelector(state => state.user.clock_offset);
  const hasValidSubscription = useSelector(state => !!state.user.subscription?.is_valid);
  const previouslyHadValidSubscription = usePrevious(hasValidSubscription);
  const subscriptionValidityChanged =
    typeof previouslyHadValidSubscription === 'boolean' &&
    hasValidSubscription !== previouslyHadValidSubscription;
  const hasNewSubscription = useSelector(state => !!state.user.subscription?.is_new);
  const apolloClient = useApolloClient();

  // Don't run the access check until we know who the user is, either through login credentials or the clientId.
  const readyForAccessCheck = forceSsr || (!previewOnly && (isLoggedIn || clientId));
  const meteredAccessQuery = useQuery(MeteredAccessQuery, {
    variables: {
      id,
      type,
      isInitialPage,
      token,
      // Don't send the clientId if we are logged in. This is so that on first render we can run the access check before
      // The clientId has loaded, without causing a requery when clientId becomes available.
      clientId: isLoggedIn ? null : clientId,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : null,
      hasValidSubscriptionHint: hasValidSubscription,
      _authenticated: true,
    },
    fetchPolicy: 'cache-first',
    ssr: false,
    skip: forceSsr || !readyForAccessCheck,
  });

  // If the user loses their premium membership after the access check has been
  // done then recheck automatically. This will cause the paywall to appear.
  // We also hide the paywall if the user gains premium membership UNLESS it was purchased on this page.
  // Thats so we can let the user see the confirmation message and press a button to trigger meteredAccessQuery.refetch
  useEffect(
    () => {
      if (
        readyForAccessCheck &&
        mounted &&
        !meteredAccessQuery.loading &&
        subscriptionValidityChanged &&
        !hasNewSubscription
      ) {
        meteredAccessQuery.refetch();
      }
    },
    [
      readyForAccessCheck,
      mounted,
      meteredAccessQuery,
      subscriptionValidityChanged,
      hasNewSubscription,
    ],
  );

  const accessPolicy = meteredAccessQuery.data?.MeteredAccess
    ? meteredAccessQuery.data.MeteredAccess
    : null;

  let accessTokenExpired = false;

  if (accessPolicy?.validUntil) {
    const now = new Date().getTime() - clockOffset;
    const validUntil = new Date(accessPolicy.validUntil).getTime();
    if (now >= validUntil) {
      accessTokenExpired = true;
    }
  }

  useEffect(() => {
    if (meteredAccessQuery && accessTokenExpired) {
      meteredAccessQuery.refetch();
    }
  });

  let contentQueryGql;
  let gqlContentType;
  if (type === CONTENT_TYPES.RECIPE) {
    contentQueryGql = RecipeQuery;
    gqlContentType = 'Recipe';
  } else {
    contentQueryGql = ContentItemQuery;
    gqlContentType = 'ContentItem';
  }
  const contentQuery = useQuery(contentQueryGql, {
    variables: {
      id,
      token: accessPolicy?.accessToken || null,
      displayType,
      isInitialPage,
    },
    fetchPolicy: previewOnly ? 'cache-only' : 'cache-first',
    // On the client, only run after the metered access check has returned
    skip: mounted && (!readyForAccessCheck || meteredAccessQuery.loading || accessTokenExpired),
  });
  const contentData = contentQuery.data ? contentQuery.data[gqlContentType] : null;

  // While loading, we can use the data from the previous query so long as it is for the same content ID.
  const previousContentData =
    contentQuery.previousData && contentQuery.previousData[gqlContentType]?.id === id
      ? contentQuery.previousData[gqlContentType]
      : null;

  // Not sure why apollo client isn't returning partial data. For now we'll just raid the cache directly.
  let preview = {};
  if (typeof window !== 'undefined' && !contentData && !contentQuery.error) {
    // We need to use interfaces rather than the graphql-s2s library. Until then, we'll need to search both Content and Recipe types :(
    const cachedContentData =
      apolloClient.readFragment(
        { fragment: RecipePreviewFragment, id: `Recipe:${id}:full` },
        true,
      ) ||
      apolloClient.readFragment(
        {
          fragment: ContentPreviewFragment,
          id: `Content:${id}:full`,
        },
        true,
      ) ||
      apolloClient.readFragment(
        {
          fragment: ContentPreviewFragment,
          id: `Content:${id}:preview`,
        },
        true,
      ) ||
      apolloClient.readFragment(
        {
          fragment: RecipePreviewFragment,
          id: `Recipe:${id}:preview`,
        },
        true,
      ) ||
      {};

    let cachedBookData;
    if (cachedContentData.book && cachedContentData.book.isbn) {
      cachedBookData = apolloClient.readFragment(
        { fragment: BookPreviewFragment, id: `Book:${cachedContentData.book.isbn}` },
        true,
      );
    }
    preview = { ...cachedContentData, book: cachedBookData };
  }

  useEffect(
    () => {
      if (!previewOnly) {
        if (contentData) {
          dispatch(setRecipe(contentData));
          // eslint-disable-next-line camelcase
          const adOptOut = contentData.book?.advertising_opt_out;
          if (typeof adOptOut === 'boolean') {
            dispatch(setAdvertVisibility(!adOptOut));
          }
        }
        if (accessPolicy) {
          // For web we currently show the metered paywall whenever there is a quota.
          // If the quota is exceeded the metered paywall should block access
          if (accessPolicy.hasQuota && accessPolicy.canView) {
            dispatch(showMeteredAccessBanner({ accessPolicy, extended: true }));
          } else {
            dispatch(hideMeteredAccessBanner({ accessPolicy }));
          }
        }
      }
    },
    [previewOnly, contentData, accessPolicy, dispatch],
  );

  // Create a content structure to hang skeletons off
  const emptyRecipe = {
    authors: preview.authors || [],
    book: preview.book || { image_permission: true, authors: [] },
    chapter: preview.chapter || 0,
    content_raw:
      type === CONTENT_TYPES.RECIPE ? ['subsection#intro', 'subsection#preparation'] : [''],
    graphics: preview.graphics || [],
    id,
    ingredients_raw: [''],
    ingredients_list_units: [],
    intro_raw: '',
    is_skeleton: true,
    next: preview.next || [],
    output: preview.output || [],
    prev: preview.prev || [],
    primary_image: preview.primary_image || { crops: [] },
    procedures_raw: [''],
    ratings: {},
    recipe_time: preview.recipe_time,
    skill: preview.skill,
    subtitle: preview.subtitle || [],
    subtitle_raw: preview.subtitle_raw || [],
    title: preview.title || [],
    title_raw: preview.title_raw || [],
    title_with_prefix: preview.title_with_prefix || [],
    tools_raw: '',
  };
  const bestAvailableContentData = contentData || previousContentData || emptyRecipe;

  // Update the ToC position
  const { chapter, book } = bestAvailableContentData;
  useEffect(
    () => {
      if (!previewOnly && book?.isbn) {
        dispatch(
          setSelectedTableOfContents({
            id,
            chapter,
            book,
          }),
        );
      }
    },
    [chapter, book, id, dispatch, previewOnly],
  );

  if (contentQuery.error) {
    return <ErrorRouter error={contentQuery.error} />;
  }

  let paywallError = null;
  if (accessTokenExpired && !meteredAccessQuery.loading) {
    paywallError = 'TOKEN_EXPIRED';
  } else if (meteredAccessQuery?.error) {
    paywallError =
      meteredAccessQuery.error.code || meteredAccessQuery.error.graphQLErrors[0]?.extensions?.code;
  }
  const commonProps = {
    alacarte: !!book?.alacarte,
    canCopy: accessPolicy?.canCopy,
    canPrint: accessPolicy?.canPrint,
    checkingAccess: !readyForAccessCheck || meteredAccessQuery.loading,
    enableSidebarAd,
    enableVoice: accessPolicy?.enableVoice === true,
    forceSsr,
    isInitialPage,
    loading: contentQuery.loading,
    paywallError,
    refreshAccessPolicy: meteredAccessQuery.refetch,
    usingAdminAccess: accessPolicy?.usingAdminAccess,
  };
  let view;
  switch (type) {
    case CONTENT_TYPES.RECIPE:
      view = (
        <Recipe previewOnly={previewOnly} recipe={bestAvailableContentData} {...commonProps} />
      );
      break;
    case CONTENT_TYPES.REFERENCE:
      view = (
        <Reference
          previewOnly={previewOnly}
          reference={bestAvailableContentData}
          {...commonProps}
        />
      );
      break;
    case CONTENT_TYPES.SECTION:
    default:
      view = (
        <Section previewOnly={previewOnly} section={bestAvailableContentData} {...commonProps} />
      );
  }
  return (
    <React.Fragment>
      {previewOnly ? null : (
        <HeadContent
          contents={{
            title: bestAvailableContentData.page_title,
            description: bestAvailableContentData.description,
          }}
        />
      )}
      {view}
      {!accessPolicy || accessPolicy.canPrint ? null : (
        <div className="recipe__no-print-warning">
          The licensor does not allow printing of this title
        </div>
      )}
    </React.Fragment>
  );
};

ContentView.propTypes = {
  id: PropTypes.string.isRequired,
  isInitialPage: PropTypes.bool,
  token: PropTypes.string,
  type: PropTypes.string.isRequired,
  previewOnly: PropTypes.bool,
  enableSidebarAd: PropTypes.bool,
  forceSsr: PropTypes.bool,
};

ContentView.defaultProps = {
  isInitialPage: false,
  token: null,
  previewOnly: false,
  enableSidebarAd: false,
  forceSsr: false,
};

ContentView.whyDidYouRender = false;

export default React.memo(ContentView);
